import {
  Box,
  createTheme,
  CssBaseline,
  Stack,
  ThemeOptions,
  ThemeProvider,
} from "@mui/material";
import { deDE, enUS, Localization } from "@mui/material/locale";
import {
  AppShell,
  EmployeeAvatar,
  LanguageSwitcher,
  tngTheme,
} from "@tngtech/tng-ui";
import { useContext, useMemo } from "react";
import { Outlet } from "react-router";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useUserEmailAddress } from "../auth/useUserEmailAddress.ts";
import { Footer } from "../components/Footer";
import { SidebarComponent } from "../components/SidebarComponent";
import { RuntimeConfigContext } from "../config/RuntimeConfigContext";
import { DetailPanel } from "../detailPanel/DetailPanel.tsx";
import { DetailPanelContext } from "../detailPanel/DetailPanelContext.ts";
import { useDetailPanelState } from "../detailPanel/useDetailPanelState.tsx";
import { useEligibleSenderIds } from "../hooks/useEligibleSenderIds.ts";
import { useGetEmployees } from "../hooks/useGetEmployees.ts";
import {
  InternationalizationContext,
  Language,
} from "../i18n/InternationalizationContext";
import { useTranslator } from "../i18n/translator";
import { useLocalizedValidationMessages } from "../i18n/useLocalizedValidationMessages.ts";
import { SnackbarComponent } from "../snackbar/SnackbarComponent.tsx";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { useSnackbarState } from "../snackbar/useSnackbarState.ts";
import { LinkAdapter } from "./LinkAdapter.tsx";

const muiLocales = {
  DE: deDE,
  EN: enUS,
} satisfies Record<Language, Localization>;

export const App = () => {
  const { language, setLanguage } = useContext(InternationalizationContext);
  const theme = useMemo(
    () =>
      createTheme(
        // Checking a whole Theme against ThemeOptions doubles the type check time
        tngTheme({ defaultLinkComponent: LinkAdapter }) as ThemeOptions,
        muiLocales[language],
      ),
    [language],
  );
  const translator = useTranslator();
  const userEmail = useUserEmailAddress();
  const { isTestSystem } = useContext(RuntimeConfigContext);
  const { isAuthenticated } = useAuthentication();
  useLocalizedValidationMessages();
  useGetEmployees();
  useEligibleSenderIds();

  const { detailPanelState, detailPanelContext } = useDetailPanelState();
  const { snackbar, closeSnackbar, snackbarContext } = useSnackbarState();

  return (
    <SnackbarContext.Provider value={snackbarContext}>
      <DetailPanelContext.Provider value={detailPanelContext}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppShell>
            <AppShell.Topbar
              projectTitle={translator(
                isTestSystem ? "appTitleTest" : "appTitle",
              )}
              color={isTestSystem ? "secondary" : undefined}
              apps={isAuthenticated ? undefined : []}
            >
              <LanguageSwitcher
                availableLanguages={["DE", "EN"]}
                changeLanguage={(selectedLanguage) =>
                  setLanguage(selectedLanguage)
                }
                currentlySelectedLanguage={language}
              />
              <EmployeeAvatar email={userEmail} />
            </AppShell.Topbar>
            <AppShell.Sidebar>
              <SidebarComponent />
            </AppShell.Sidebar>
            <AppShell.Main>
              <Stack
                direction="row"
                alignItems="stretch"
                height={`calc(100vh - ${theme.spacing(8)})`}
              >
                <Stack height="100%" width="100%" minWidth={0}>
                  <Box sx={{ overflow: "scroll" }}>
                    <Outlet />
                  </Box>
                  <Footer />
                </Stack>
                <DetailPanel open={detailPanelState.open}>
                  {detailPanelState.content}
                </DetailPanel>
              </Stack>
              <SnackbarComponent
                id={snackbar.id}
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
              />
            </AppShell.Main>
          </AppShell>
        </ThemeProvider>
      </DetailPanelContext.Provider>
    </SnackbarContext.Provider>
  );
};
